import React, { useEffect, useState } from "react";

interface InputDebounceProps extends Omit<
  React.InputHTMLAttributes<HTMLInputElement>,
  "onChange" | "value"
> {
  value: string | number;
  onChange: (value: string | number) => void;
  delay?: number;
}

const InputDebounce = ({ value, onChange, delay = 400, ...props }: InputDebounceProps) => {
  const [internalValue, setInternalValue] = useState(value);

  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  useEffect(() => {
    const handler = setTimeout(() => {
      if (internalValue !== value) {
        onChange(internalValue);
      }
    }, delay);
    return () => clearTimeout(handler);
  }, [internalValue]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBlur = (event: React.FocusEvent<HTMLInputElement, Element>) => {
    if (props.onBlur) props.onBlur(event);
    if (onChange) onChange(internalValue);
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (props.onKeyDown) props.onKeyDown(event);
    if (event.key === "Enter") {
      if (onChange) onChange(internalValue);
    }
  };

  return (
    <input
      {...props}
      value={internalValue}
      onChange={(e) => setInternalValue(e.target.value)}
      onBlur={handleBlur}
      onKeyDown={handleKeyDown}
    />
  );
};

export default InputDebounce;
